import React from 'react'
import classNames from 'classnames'
import { isEqual, startOfDay } from 'date-fns'
import { AsP, EyebrowSM, Footer, HeadingXS, Text } from '@/atoms/Text'
import { useLocale } from '@/utils/LocaleUtil'
import {
  getHolidayNameStyle,
  getDateTextColor,
  getBorderColor,
  getBackgroundColor,
} from '@/views/TicketCheckoutViews/ShowtimesView/components/HolidayDates/holidayCardStyles'
import type { Holiday } from '@/views/TicketCheckoutViews/ShowtimesView/components/HolidayDates/holidayConstants'

interface HolidayDateCardProps {
  disabled: boolean
  date: Date
  onClick: () => void
  selected: boolean
  holiday: Holiday
  specialDate?: Date
}

export const HolidayDateCard: React.FC<HolidayDateCardProps> = ({
  disabled,
  date,
  onClick,
  selected,
  holiday,
  specialDate,
}) => {
  const { locale } = useLocale()

  const getFontSizeClass = (name: string) => {
    const length = name.length
    if (length <= 6) return '!text-xl'
    return '!text-[11px]'
  }

  return (
    <div className="relative">
      <div
        className={classNames(
          'w-[72px] h-24 relative rounded-xl hover:scale-105 hover:bg-white bg-core-gray-200 text-core-gray-900 border-[1.5px] hover:!z-30',
          {
            'cursor-not-allowed': disabled,
            'cursor-pointer': !disabled,
            '!bg-core-gray-950 shadow-[0_8px_16px_-1px_rgba(0,0,0,0.08)]': selected,
          },
        )}
        style={{ ...getBorderColor(holiday, selected), ...getBackgroundColor(holiday, selected) }}
        onClick={onClick}
      >
        <div className="flex h-full flex-col justify-center pt-0.5 text-center">
          <Text
            as={AsP}
            className={classNames(`${getFontSizeClass(holiday.name)} !leading-[1] !font-nomadic line-clamp-2 px-0.5`, {
              '!bg-clip-text': !selected,
            })}
            style={getHolidayNameStyle(holiday, selected)}
          >
            {holiday.name}
          </Text>
          <HeadingXS as={AsP} weight="bold" style={getDateTextColor(holiday, selected)} className="!text-[32px]">
            {date.getDate()}
          </HeadingXS>
          <EyebrowSM
            color="core-gray-700"
            className={classNames('uppercase', {
              '!text-core-gray-400': selected,
            })}
          >
            {date.toLocaleString(locale, { weekday: 'short' })}
          </EyebrowSM>
        </div>
        {specialDate && isEqual(startOfDay(date), startOfDay(specialDate)) ? (
          <Footer
            italic={true}
            color={selected ? 'white' : 'copper'}
            weight={'medium'}
            className="absolute bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap text-center text-[8px]"
          >
            Early Release
          </Footer>
        ) : null}
        {selected && (
          <div
            className={classNames('absolute bottom-1 right-1/2 h-1 w-3 translate-x-2/4 rounded-full bg-oxide-bright')}
          ></div>
        )}
      </div>
    </div>
  )
}
